import { useState } from "react";
import type { FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { Building2, Mail, MapPin, Send } from "lucide-react";
import { ContactFormTurnstile, useContactFormTurnstile } from "@/app/components/ContactFormTurnstile";
import { CONTACT_FORM_PUBLIC_ERROR, postContactApi } from "@/lib/contactSubmit";
import { isValidWorkEmail, LIMITS } from "@/lib/formValidation";
import { Seo } from "../components/Seo";

export function Contact() {
  const [searchParams] = useSearchParams();
  const planRedirect = searchParams.get("plan")?.toLowerCase().trim();
  if (planRedirect === "pilot") {
    return <Navigate to="/get-started" replace />;
  }
  if (planRedirect === "essential" || planRedirect === "pro" || planRedirect === "enterprise") {
    return <Navigate to={`/request-demo?plan=${planRedirect}`} replace />;
  }

  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    turnstileSiteKey,
    containerRef,
    turnstileReady,
    getToken,
    resetTurnstile,
    turnstileLoadError,
  } = useContactFormTurnstile();

  function validateContactFields(input: {
    name: string;
    email: string;
    company: string;
    topic: string;
    message: string;
  }): string | null {
    const { name, email, company, topic, message } = input;
    if (name.length < LIMITS.name.min || name.length > LIMITS.name.max) {
      return `Name must be between ${LIMITS.name.min} and ${LIMITS.name.max} characters.`;
    }
    if (!isValidWorkEmail(email)) {
      return "Enter a valid email address.";
    }
    if (company.length < LIMITS.company.min || company.length > LIMITS.company.max) {
      return `Company must be between ${LIMITS.company.min} and ${LIMITS.company.max} characters.`;
    }
    const allowedTopics = ["demo", "security", "pricing", "partner", "case-study", "other"];
    if (!allowedTopics.includes(topic)) {
      return "Choose a valid topic.";
    }
    if (message.length < LIMITS.contactMessage.min || message.length > LIMITS.contactMessage.max) {
      return `Message must be between ${LIMITS.contactMessage.min} and ${LIMITS.contactMessage.max} characters.`;
    }
    return null;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitError(null);
    const form = e.currentTarget;
    const data = new FormData(form);
    const name = String(data.get("name") ?? "").trim();
    const email = String(data.get("email") ?? "").trim();
    const company = String(data.get("company") ?? "").trim();
    const topic = String(data.get("topic") ?? "").trim();
    const message = String(data.get("message") ?? "").trim();

    const validationError = validateContactFields({ name, email, company, topic, message });
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    const turnstileToken = getToken();
    if (turnstileSiteKey && (!turnstileToken || turnstileToken.length === 0)) {
      setSubmitError("Complete the CAPTCHA below, then try again.");
      return;
    }

    setSubmitting(true);
    try {
      const payload: Record<string, string> = {
        type: "contact",
        name,
        email,
        company,
        topic,
        message,
      };
      if (turnstileToken) payload.turnstileToken = turnstileToken;

      const result = await postContactApi(payload);
      if (!result.ok) {
        if (turnstileSiteKey) resetTurnstile();
        setSubmitError(result.error ?? CONTACT_FORM_PUBLIC_ERROR);
        return;
      }
      setSent(true);
      if (turnstileSiteKey) resetTurnstile();
      form.reset();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Seo
        title="Contact"
        description="Contact DeskReplay for enterprise demos, security architecture reviews, procurement packages, and questions about deployments and plans."
        canonicalPath="/contact"
      />
      <div className="bg-slate-950 min-h-screen pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <p className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-3">Contact</p>
          <h1 className="text-slate-100 text-4xl font-extrabold tracking-tight mb-4">Talk to DeskReplay</h1>
          <p className="text-on-dark-muted text-lg leading-relaxed">
            Enterprise demos, security architecture reviews, and procurement packages. We respond within two business days
            for qualified inquiries.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-2 space-y-8">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
              <div className="flex items-start gap-3">
                <Mail className="text-teal-400 shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-slate-200 font-semibold mb-1">Email</p>
                  <a href="mailto:sales@deskreplay.com" className="text-teal-400 hover:text-teal-300 text-sm">
                    sales@deskreplay.com
                  </a>
                  <p className="text-on-dark-muted text-xs mt-3">
                    Legal & privacy:{" "}
                    <a href="mailto:legal@deskreplay.com" className="text-on-dark-muted hover:text-teal-400">
                      legal@deskreplay.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
              <div className="flex items-start gap-3">
                <MapPin className="text-teal-400 shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="text-slate-200 font-semibold mb-1">Office</p>
                  <address className="text-on-dark-muted text-sm not-italic leading-relaxed">
                    RWebSoft Technologies Pvt Ltd
                    <br />
                    Sankalp Tower, Suite 50
                    <br />
                    Vaishali Nagar, Jaipur, 302021
                  </address>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 text-on-dark-muted text-sm">
              <Building2 size={18} className="shrink-0 mt-0.5" />
              <p>
                Prefer paper procurement or vendor registration packs? Reference our{" "}
                <Link to="/legal/terms-of-service" className="text-teal-500/90 hover:text-teal-400">
                  Terms of Service
                </Link>{" "}
                and include your billing entity on first contact.
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <form
              onSubmit={onSubmit}
              className="rounded-2xl border border-teal-500/15 bg-gradient-to-br from-slate-900/80 to-slate-950 p-8 sm:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-5 mb-5">
                <label className="flex flex-col gap-2">
                  <span className="text-slate-300 text-sm font-medium">Name *</span>
                  <input
                    name="name"
                    required
                    minLength={LIMITS.name.min}
                    maxLength={LIMITS.name.max}
                    className="rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50"
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </label>
                <label className="flex flex-col gap-2">
                  <span className="text-slate-300 text-sm font-medium">Work email *</span>
                  <input
                    name="email"
                    type="email"
                    required
                    maxLength={LIMITS.email.max}
                    className="rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50"
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                </label>
              </div>

              <label className="flex flex-col gap-2 mb-5">
                <span className="text-slate-300 text-sm font-medium">Company *</span>
                <input
                  name="company"
                  required
                  minLength={LIMITS.company.min}
                  maxLength={LIMITS.company.max}
                  className="rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50"
                  placeholder="Organization name"
                  autoComplete="organization"
                />
              </label>

              <label className="flex flex-col gap-2 mb-5">
                <span className="text-slate-300 text-sm font-medium">Topic</span>
                <select
                  name="topic"
                  className="rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50"
                  defaultValue="demo"
                >
                  <option value="demo">Enterprise demo</option>
                  <option value="security">Security / architecture review</option>
                  <option value="pricing">Pricing & licensing</option>
                  <option value="partner">Partner / reseller</option>
                  <option value="case-study">Case study or PDF</option>
                  <option value="other">Other</option>
                </select>
              </label>

              <label className="flex flex-col gap-2 mb-6">
                <span className="text-slate-300 text-sm font-medium">Message *</span>
                <textarea
                  name="message"
                  required
                  rows={5}
                  minLength={LIMITS.contactMessage.min}
                  maxLength={LIMITS.contactMessage.max}
                  className="rounded-lg bg-slate-950 border border-slate-500 text-slate-100 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500/50 resize-y min-h-[120px]"
                  placeholder="Describe your environment, regions, and compliance context."
                />
              </label>

              {turnstileSiteKey ? (
                <ContactFormTurnstile containerRef={containerRef} loadError={turnstileLoadError} />
              ) : null}

              {submitError && (
                <p className="text-red-400/90 text-sm mb-4" role="alert">
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting || (!!turnstileSiteKey && !turnstileReady)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-500 disabled:opacity-60 disabled:pointer-events-none text-white px-8 py-3.5 rounded-xl font-semibold text-sm transition-all hover:shadow-lg hover:shadow-teal-500/25"
              >
                <Send size={16} />
                {submitting ? "Sending…" : "Send message"}
              </button>

              {sent && (
                <p className="text-emerald-400/90 text-sm mt-4">
                  Thank you. We received your message and will reply within two business days when possible.
                </p>
              )}

              <p className="text-on-dark-muted text-xs mt-6 leading-relaxed">
                Submissions are delivered to our sales inbox. For GDPR-related requests, use privacy@deskreplay.com.
              </p>
            </form>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}
